import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  MoreHorizontal,
  PauseCircle,
  RefreshCcw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from "randmarcomps";
import {
  useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSkuMutation,
  usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSkuMutation,
} from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Opportunity, OpportunityDetail } from "../../stores/opportunities/Opportunity";
import { Product } from "../../stores/product/Product";
import { RootStoreContext } from "../../stores/rootStore";
import ProductDetailsModal from "../products/ProductDetailsModal";
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";
import CurrencyUtils from "../util/CurrencyUtils";
import { FormatDateFromString } from "../util/DateTimeUtil";

interface OpportunityDetailsGridProps {
  defaultRowsPerPage?: number;
  opportunityDetails?: OpportunityDetail[];
  productOpportunities?: Opportunity[];
  product?: Product;
  onChanged?: () => void;
}

const rowsPerPageOptions = [10, 20, 50, 100] as const;

const parseOpportunityDateOrNull = (dateValue?: string | null) => {
  if (!dateValue) return null;

  if (/^\\d{8}$/.test(dateValue)) {
    const year = Number.parseInt(dateValue.slice(0, 4), 10);
    const month = Number.parseInt(dateValue.slice(4, 6), 10);
    const day = Number.parseInt(dateValue.slice(6, 8), 10);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(dateValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isOpportunityActive = (opp: Opportunity) => {
  if (!opp.endDate) return false;
  const parsedEndDate = parseOpportunityDateOrNull(opp.endDate);
  if (!parsedEndDate) return false;
  return Boolean(opp.active) && parsedEndDate >= new Date();
};

type EditableOpportunityLine = {
  bidNumber: string;
  randmarSKU: string;
  mpnLabel: string;
  bidPrice?: number;
  bidPriceType?: string;
};

const ProductThumbnail: React.FC<{ randmarSKU: string; alt: string }> = ({ randmarSKU, alt }) => {
  const [hasError, setHasError] = React.useState(false);
  const src = `https://api.randmar.io/Product/${encodeURIComponent(randmarSKU)}/Image`;

  if (!randmarSKU || hasError) {
    return (
      <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-400">
        <ImageIcon className="h-5 w-5" aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="h-14 w-14 rounded-xl border border-slate-200 bg-white object-contain p-1"
      onError={() => setHasError(true)}
    />
  );
};

const OpportunityDetailsGrid: React.FC<OpportunityDetailsGridProps> = ({
  opportunityDetails,
  productOpportunities,
  product,
  defaultRowsPerPage,
  onChanged,
}) => {
  const { applicationId } = useManufacturer();
  const { userStore, categoriesStore } = React.useContext(RootStoreContext);
  const { toast } = useToast();
  const isSuperAdmin = userStore.isSuperAdmin();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage || 20);

  const [editingLine, setEditingLine] = React.useState<EditableOpportunityLine | null>(null);
  const [chosenPriceType, setChosenPriceType] = React.useState("ItemRebate");
  const [chosenBidPrice, setChosenBidPrice] = React.useState<number | undefined>(undefined);

  const [editOpportunityLine, editOpportunityLineState] =
    usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSkuMutation();
  const [deleteOpportunityLine] =
    useDeleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSkuMutation();

  React.useEffect(() => {
    if (!editingLine) return;
    setChosenPriceType(editingLine.bidPriceType || "ItemRebate");
    setChosenBidPrice(editingLine.bidPrice);
  }, [editingLine]);

  const hasOpportunityDetails = (opportunityDetails?.length ?? 0) > 0;
  const hasProductOpportunities = Boolean(product && productOpportunities && productOpportunities.length > 0);

  const rows = React.useMemo(() => {
    if (hasOpportunityDetails) return opportunityDetails || [];
    if (hasProductOpportunities) return productOpportunities || [];
    return [];
  }, [hasOpportunityDetails, hasProductOpportunities, opportunityDetails, productOpportunities]);

  const totalRows = rows.length;
  const safeRowsPerPage = Math.max(rowsPerPage, 1);
  const totalPages = Math.max(Math.ceil(totalRows / safeRowsPerPage), 1);
  const safePage = Math.min(page, totalPages - 1);
  const startRow = safePage * safeRowsPerPage;
  const endRow = Math.min(startRow + safeRowsPerPage, totalRows);

  const handlePreviousPage = React.useCallback(() => setPage((prev) => Math.max(prev - 1, 0)), []);
  const handleNextPage = React.useCallback(() => setPage((prev) => Math.min(prev + 1, totalPages - 1)), [totalPages]);

  const openEditDialogForOpportunityDetail = React.useCallback(
    (row: OpportunityDetail) => {
      if (!row.bidNumber || !row.randmarSKU) return;
      setEditingLine({
        bidNumber: row.bidNumber,
        randmarSKU: row.randmarSKU,
        mpnLabel: row.product?.mpn || row.randmarSKU,
        bidPrice: row.bidPrice,
        bidPriceType: row.bidPriceType,
      });
    },
    []
  );

  const openEditDialogForProductOpportunity = React.useCallback(
    (row: Opportunity) => {
      if (!row.bidNumber) return;
      const sku = row.randmarSKU || product?.randmarSKU;
      if (!sku) return;
      setEditingLine({
        bidNumber: row.bidNumber,
        randmarSKU: sku,
        mpnLabel: product?.mpn || sku,
        bidPrice: row.bidPrice,
        bidPriceType: row.bidPriceType,
      });
    },
    [product?.mpn, product?.randmarSKU]
  );

  const handleConfirmEdit = React.useCallback(async () => {
    if (!editingLine) return;
    if (!isSuperAdmin) return;
    if (!chosenBidPrice || chosenBidPrice <= 0) return;

    try {
      const success = await editOpportunityLine({
        routeManufacturerId: applicationId,
        bidNumber: editingLine.bidNumber,
        randmarSku: editingLine.randmarSKU,
        bidPrice: chosenBidPrice,
        bidPriceType: chosenPriceType,
      }).unwrap();

      if (success) {
        toast({ variant: "success", title: "Updated", description: "Opportunity pricing updated." });
        onChanged?.();
      } else {
        toast({ variant: "destructive", title: "Error", description: "Could not update opportunity pricing." });
      }
    } catch (error) {
      console.error("Failed to update opportunity pricing:", error);
      toast({ variant: "destructive", title: "Error", description: "Could not update opportunity pricing." });
    } finally {
      setEditingLine(null);
    }
  }, [
    applicationId,
    chosenBidPrice,
    chosenPriceType,
    editOpportunityLine,
    editingLine,
    isSuperAdmin,
    onChanged,
    toast,
  ]);

  const handleConfirmDelete = React.useCallback(
    async (bidNumber: string, randmarSKU: string) => {
      if (!isSuperAdmin) return;
      if (!bidNumber || !randmarSKU) return;

      try {
        const success = await deleteOpportunityLine({
          routeManufacturerId: applicationId,
          bidNumber,
          randmarSku: randmarSKU,
        }).unwrap();

        if (success) {
          toast({ variant: "success", title: "Removed", description: "Part number removed from opportunity." });
          onChanged?.();
        } else {
          toast({ variant: "destructive", title: "Error", description: "Could not remove part number from opportunity." });
        }
      } catch (error) {
        console.error("Failed to remove part number from opportunity:", error);
        toast({ variant: "destructive", title: "Error", description: "Could not remove part number from opportunity." });
      }
    },
    [applicationId, deleteOpportunityLine, isSuperAdmin, onChanged, toast]
  );

  if (!hasOpportunityDetails && !hasProductOpportunities) {
    return null;
  }

  const renderActionsMenu = (options: {
    onEdit: () => void;
    onDelete: () => void;
    deleteLabel: string;
    deleteDescription: string;
  }) => {
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="icon" className="h-10 w-10 rounded-xl">
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                options.onEdit();
              }}
              className="cursor-pointer"
            >
              <RefreshCcw className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit details
            </DropdownMenuItem>
            {isSuperAdmin ? (
              <ButtonWithDialogConfirm
                onConfirmation={async () => options.onDelete()}
                buttonTitle="Remove"
                text={options.deleteDescription}
              >
                <DropdownMenuItem onSelect={(event) => event.preventDefault()} className="cursor-pointer text-rose-600 focus:text-rose-600">
                  <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                  {options.deleteLabel}
                </DropdownMenuItem>
              </ButtonWithDialogConfirm>
            ) : null}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      <Card className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              {hasOpportunityDetails ? (
                <TableRow className="h-12">
                  <TableHead className="min-w-[360px] text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Product
                  </TableHead>
                  <TableHead className="min-w-[220px] text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Bid pricing
                  </TableHead>
                  <TableHead className="w-56 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Signals
                  </TableHead>
                  <TableHead className="w-20 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </TableHead>
                </TableRow>
              ) : (
                <TableRow className="h-12">
                  <TableHead className="w-40 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </TableHead>
                  <TableHead className="w-40 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    End date
                  </TableHead>
                  <TableHead className="min-w-[280px] text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Opportunity
                  </TableHead>
                  <TableHead className="w-40 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Price
                  </TableHead>
                  <TableHead className="w-40 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Type
                  </TableHead>
                  <TableHead className="w-20 text-center text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </TableHead>
                </TableRow>
              )}
            </TableHeader>

            <TableBody>
              {hasOpportunityDetails
                ? (opportunityDetails || []).slice(startRow, endRow).map((row, index) => {
                  const sku = row.randmarSKU || "";
                  const categoryCode = row.product?.category || "";
                  const categoryName =
                    categoriesStore.categories.find((category) => category.categoryCode === categoryCode)?.categoryName ||
                    categoryCode ||
                    "—";

                  const state = row.product?.state || "";
                  const autoUpdate = Boolean(row.product?.autoUpdate);
                  const stateBadge =
                    state === "Active"
                      ? { label: "Active", variant: "success" as const, icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> }
                      : state === "Hold" || state === "Discontinued"
                        ? { label: state || "Hold", variant: "warning" as const, icon: <PauseCircle className="h-4 w-4" aria-hidden="true" /> }
                        : { label: state || "Unknown", variant: "secondary" as const, icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" /> };

                  return (
                    <TableRow key={`${sku}-${index}`} className="h-20 transition-colors hover:bg-slate-50/70">
                      <TableCell className="text-[15px]">
                        <div className="flex items-center gap-4">
                          <ProductThumbnail randmarSKU={sku} alt={row.product?.mpn || "Product image"} />
                          <div className="min-w-0 space-y-1">
                            <Link to={Path.product(applicationId, sku)} className="inline-block text-[18px] leading-none font-semibold text-slate-900 hover:text-slate-700 hover:underline">
                              {row.product?.mpn || "Unknown product"}
                            </Link>
                            <div className="flex flex-wrap gap-2">
                              {categoryCode ? (
                                <Link
                                  to={Path.category(applicationId, categoryCode)}
                                  className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                                >
                                  {categoryName}
                                </Link>
                              ) : (
                                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500">
                                  No category
                                </span>
                              )}
                              <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-500">
                                {row.bidNumber || "No bid"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="text-[15px]">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="text-[24px] font-semibold leading-none text-slate-900">
                              <FormattedNumber value={row.bidPrice || 0} {...CurrencyUtils.format} />
                            </p>
                            <p className="mt-2 text-[13px] text-slate-600">Type: {row.bidPriceType || "Unknown"}</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 rounded-lg px-3"
                            onClick={() => openEditDialogForOpportunityDetail(row)}
                          >
                            Edit pricing
                          </Button>
                        </div>
                      </TableCell>

                      <TableCell className="text-center">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <Badge variant={stateBadge.variant} size="sm" icon={stateBadge.icon} className="w-fit">
                            {stateBadge.label}
                          </Badge>
                          <Badge variant={row.product?.mpn ? "success" : "warning"} size="sm" className="w-fit">
                            {row.product?.mpn ? "Ready" : "Missing reference"}
                          </Badge>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white">
                                  <RefreshCcw className={autoUpdate ? "h-4 w-4 text-emerald-600" : "h-4 w-4 text-slate-400"} aria-hidden="true" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{autoUpdate ? "Auto update enabled" : "Auto update disabled"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <ProductDetailsModal randmarSKU={sku} />
                        </div>
                      </TableCell>


                      <TableCell className="text-center">
                        {renderActionsMenu({
                          onEdit: () => openEditDialogForOpportunityDetail(row),
                          onDelete: () => handleConfirmDelete(row.bidNumber || "", sku),
                          deleteLabel: "Remove",
                          deleteDescription: `Remove ${row.product?.mpn || sku} from opportunity ${row.bidNumber || ""}?`,
                        })}
                      </TableCell>
                    </TableRow>
                  );
                })
                : (productOpportunities || []).slice(startRow, endRow).map((row, index) => {
                  const active = isOpportunityActive(row);
                  const defaultStatus = Boolean(row.canBeSetAsDefault);
                  const hasDefaults = Boolean(row.defaultResellers);

                  const statusBadges = [
                    active
                      ? { label: "Active", variant: "success" as const, icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> }
                      : { label: "Ended", variant: "secondary" as const, icon: <XCircle className="h-4 w-4" aria-hidden="true" /> },
                    defaultStatus
                      ? { label: "Default", variant: "secondary" as const, icon: <ShieldCheck className="h-4 w-4" aria-hidden="true" /> }
                      : null,
                    hasDefaults
                      ? { label: "Defaults set", variant: "secondary" as const, icon: <RefreshCcw className="h-4 w-4" aria-hidden="true" /> }
                      : null,
                  ].filter(Boolean) as Array<{ label: string; variant: React.ComponentProps<typeof Badge>["variant"]; icon: React.ReactNode }>;

                  const sku = row.randmarSKU || product?.randmarSKU || "";

                  return (
                    <TableRow key={`${row.bidNumber ?? "opportunity"}-${index}`} className="h-14 transition-colors hover:bg-gray-50">
                      <TableCell className="text-center">
                        <div className="flex flex-wrap justify-center gap-1">
                          {statusBadges.map((badge) => (
                            <Badge key={badge.label} variant={badge.variant} size="sm" icon={badge.icon} className="w-fit">
                              {badge.label}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>

                      <TableCell className="whitespace-nowrap text-center text-[15px] text-gray-700">
                        {FormatDateFromString(row.endDate || undefined) || "—"}
                      </TableCell>

                      <TableCell className="text-[15px]">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Link to={Path.opportunity(applicationId, row.bidNumber)} className="font-medium text-gray-800 hover:underline">
                                <span className="font-semibold">{row.bidNumber}</span> {row.opportunity}
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">Details</p>
                              <p className="mt-2 text-[13px] text-gray-700">Start: {FormatDateFromString(row.startDate || undefined) || "—"}</p>
                              <p className="text-[13px] text-gray-700">Last update: {FormatDateFromString(row.lastUpdate || undefined) || "—"}</p>
                              {row.description ? <p className="mt-2 text-[13px] text-gray-700">Description: {row.description}</p> : null}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>

                      <TableCell className="whitespace-nowrap text-center text-[15px] text-gray-700">
                        <FormattedNumber value={row.bidPrice || 0} {...CurrencyUtils.format} />
                      </TableCell>

                      <TableCell className="text-center text-[15px] text-gray-700">{row.bidPriceType || "—"}</TableCell>

                      <TableCell className="text-center">
                        {renderActionsMenu({
                          onEdit: () => openEditDialogForProductOpportunity(row),
                          onDelete: () => handleConfirmDelete(row.bidNumber || "", sku),
                          deleteLabel: "Remove",
                          deleteDescription: `Remove opportunity ${row.bidNumber || ""} from ${product?.mpn || sku}?`,
                        })}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        {totalRows > safeRowsPerPage ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
            <p className="text-[13px] text-gray-500">
              Showing <span className="font-semibold text-gray-800">{startRow + 1}</span>–
              <span className="font-semibold text-gray-800">{endRow}</span> of{" "}
              <span className="font-semibold text-gray-800">{totalRows}</span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={String(safeRowsPerPage)}
                onValueChange={(value) => {
                  setRowsPerPage(Number.parseInt(value, 10));
                  setPage(0);
                }}
              >
                <SelectTrigger className="h-10 w-[140px] rounded-xl">
                  <SelectValue placeholder="Rows" />
                </SelectTrigger>
                <SelectContent>
                  {rowsPerPageOptions.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" className="h-10 rounded-xl px-3" onClick={handlePreviousPage} disabled={safePage === 0}>
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-10 rounded-xl px-3" onClick={handleNextPage} disabled={safePage >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      <Dialog open={Boolean(editingLine)} onOpenChange={(isOpen) => !isOpen && !editOpportunityLineState.isLoading && setEditingLine(null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Edit opportunity pricing</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Line item</p>
              <p className="mt-1 text-[15px] font-medium text-slate-900">{editingLine?.mpnLabel}</p>
              <p className="mt-1 text-[13px] text-slate-500">{editingLine?.bidNumber}</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="opportunity-price-type">Price type</Label>
              <Select value={chosenPriceType} onValueChange={setChosenPriceType}>
                <SelectTrigger id="opportunity-price-type" className="h-11 rounded-xl">
                  <SelectValue placeholder="Select a price type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ItemRebate">Rebate amount</SelectItem>
                  {isSuperAdmin ? <SelectItem value="ItemCost">Item cost</SelectItem> : null}
                  {isSuperAdmin ? <SelectItem value="ItemCostGP">Item cost GP</SelectItem> : null}
                  {isSuperAdmin ? <SelectItem value="Fixed">Fixed pricing</SelectItem> : null}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="opportunity-bid-price">Amount</Label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-400">$</span>
                <Input
                  id="opportunity-bid-price"
                  type="number"
                  placeholder="0.00"
                  value={chosenBidPrice ?? ""}
                  onChange={(event) => setChosenBidPrice(event.target.value ? Number(event.target.value) : undefined)}
                  autoComplete="off"
                  className="h-11 rounded-xl pl-7"
                  disabled={editOpportunityLineState.isLoading}
                />
              </div>
            </div>

            {!isSuperAdmin ? (
              <Alert variant="warning" className="rounded-2xl">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Access required</AlertTitle>
                <AlertDescription>You need to be a Super Admin to edit opportunity pricing.</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <DialogFooter className="flex flex-wrap justify-between gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setEditingLine(null)} disabled={editOpportunityLineState.isLoading}>
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-xl"
              onClick={handleConfirmEdit}
              disabled={!isSuperAdmin || editOpportunityLineState.isLoading || !chosenBidPrice || chosenBidPrice <= 0 || !chosenPriceType}
            >
              {editOpportunityLineState.isLoading ? <RefreshCcw className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OpportunityDetailsGrid;

